import React from 'react';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { fetchUserDetails } from '../services/api';

/**
 * SourceLink
 * - Given a device `source` (e.g. 'TGStream' or 'TGMaxx'), look up the current
 *   user's tenant mapping and show the tenant.displayName and tenant.portalUrl
 *   when available. Falls back to a known portal URL map or the raw source.
 */
export default function SourceLink({ source, sx, onClick }: { source?: string | null; sx?: any; onClick?: (e: React.MouseEvent) => void }) {
  const { data: userDetails } = useQuery({ queryKey: ['user', 'details'], queryFn: fetchUserDetails, staleTime: 1000 * 60 * 5, retry: false, refetchOnWindowFocus: false });

  const tenantEntry = React.useMemo(() => {
    if (!userDetails || !source) return null;
    // userDetails.userTenants[] -> each entry has .tenant
    return (userDetails.userTenants ?? []).find((ut: any) => ut?.tenant?.name === source) ?? null;
  }, [userDetails, source]);

  const label = tenantEntry?.tenant?.displayName ?? tenantEntry?.tenant?.display_name ?? source ?? '\u2014';

  // Try multiple locations / naming conventions for base portal URL and suffix.
  const portalBase = tenantEntry?.tenant?.portalUrl ?? null;

  // The suffix may live on the UserTenant record (tenantPortalUrlSuffix) or on tenant;
  // try several common variants to be resilient to backend naming.
  const suffix = tenantEntry?.tenantPortalUrlSuffix ?? '';

  const defaultUrls: Record<string, string> = {
    TGStream: 'https://www.skywayplatform.com/skyway-platform/login',
    TGStreamTest: 'https://test.skywayplatform.com/skyway-platform/login',
    TGStreamTL4KBR: 'https://www.skywayplatform.com/skyway-platform/login',
    TGMaxx: 'https://manage.atvbridge.com/',
  };

  // Build href: prefer portalBase, then default; append suffix if present.
  const baseCandidate = portalBase ?? (source ? defaultUrls[source] : undefined) ?? undefined;
  let href: string | undefined = undefined;
  if (baseCandidate) {
    const b = String(baseCandidate);
    const s = String(suffix ?? '');
    if (s && s.trim() !== '') {
      // join safely: remove trailing slash from base, leading slash from suffix
      const baseNoSlash = b.replace(/\/+$/g, '');
      const suffixNoLead = s.replace(/^\/+/, '');
      href = `${baseNoSlash}/${suffixNoLead}`;
    } else {
      href = b;
    }
  }

  if (href) {
    return (
      <Link href={href} target="_blank" rel="noopener" onClick={(e) => { e.stopPropagation(); onClick?.(e); }} underline="none" sx={{ fontWeight: 600, color: '#4f83cc', '&:hover': { textDecoration: 'underline' }, ...(sx ?? {}) }}>
        {label}
      </Link>
    );
  }

  // no link available — render styled text
  return (
    <Typography component="span" sx={{ fontWeight: 600, color: '#4f83cc', ...(sx ?? {}) }} onClick={(e) => { e.stopPropagation(); onClick?.(e); }}>
      {label}
    </Typography>
  );
}
